import { useState } from 'react'

import { api } from '../api/client'
import { usePermessiModuli } from '../auth/permessi'
import { ErrorBanner, PageSection, SectionedPage, Spinner } from '../components/common'
import { Jobs } from '../components/patching/Jobs'
import type { MachineFilter } from '../components/patching/Machines'
import { Machines } from '../components/patching/Machines'
import { MaintenanceWindows } from '../components/patching/MaintenanceWindows'
import { NewJobWizard } from '../components/patching/NewJobWizard'
import type { WizardPrefill } from '../components/patching/NewJobWizard'
import { Overview } from '../components/patching/Overview'
import type { PatchFilter } from '../components/patching/PatchCatalog'
import { PatchCatalog } from '../components/patching/PatchCatalog'
import { Reports } from '../components/patching/Reports'
import { Lifecycle } from '../components/patching/Lifecycle'
import { SlaPolicy } from '../components/patching/SlaPolicy'
import { Policies } from '../components/patching/Policies'
import { Exceptions } from '../components/patching/Exceptions'
import { Tickets } from '../components/patching/Tickets'
import { Coverage } from '../components/patching/Coverage'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * Sicurezza → Patching (ADR-0024): the patch manager of the fleet.
 *
 * Five sections: the overview (is the fleet patched, by environment, what to
 * install first), the machines (what each one misses and whether it can be
 * patched now), the patches (one row per missing patch, by priority), the jobs
 * (what was installed where, verified after the next inventory) and the
 * maintenance windows. «Nuovo intervento» asks what, where and when, and shows
 * machine by machine what will happen before anything is created.
 */
const SECTION_ID = { overview: 'panoramica', machines: 'macchine', patches: 'patch', jobs: 'interventi' } as const

export default function Patching() {
  const summary = useAsync(() => api.patchSummary(), [])
  const fleet = useAsync(() => api.patchFleet(), [])
  const catalog = useAsync(() => api.patchCatalog(), [])
  const jobs = useAsync(() => api.patchJobs(), [])
  const canWrite = usePermessiModuli().scrive('patching')
  const [wizard, setWizard] = useState<WizardPrefill | null>(null)
  const [machineFilter, setMachineFilter] = useState<MachineFilter>('all')
  const [patchFilter, setPatchFilter] = useState<PatchFilter>('all')
  const [openJob, setOpenJob] = useState<string | undefined>()

  function reloadAll() {
    void summary.reload()
    void fleet.reload()
    void catalog.reload()
    void jobs.reload()
  }

  function go(section: 'machines' | 'patches' | 'jobs', filter?: string) {
    if (section === 'machines') setMachineFilter((filter as MachineFilter) ?? 'all')
    if (section === 'patches') setPatchFilter((filter as PatchFilter) ?? 'all')
    if (section === 'jobs') setOpenJob(filter)
    window.location.hash = SECTION_ID[section]
  }

  const error = summary.error ?? fleet.error ?? catalog.error ?? jobs.error

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Patching')}</h1>
          <p className="page__subtitle">
            {t('Le patch mancanti di tutte le macchine e la loro installazione tramite l’agente CENSO: scegli cosa, dove e quando, un amministratore approva, e dopo l’installazione CENSO verifica che le patch non risultino più mancanti. Nessun riavvio senza il tuo consenso.')}
          </p>
        </div>
        {canWrite && <button onClick={() => setWizard({})}>{t('Nuovo intervento')}</button>}
      </header>
      <ErrorBanner error={error} />
      <SectionedPage
        sections={[
          { id: SECTION_ID.overview, label: t('Panoramica') },
          { id: SECTION_ID.machines, label: t('Macchine') },
          { id: 'copertura', label: t('Copertura') },
          { id: SECTION_ID.patches, label: t('Patch') },
          { id: SECTION_ID.jobs, label: t('Interventi') },
          { id: 'politiche', label: t('Politiche') },
          { id: 'eccezioni', label: t('Eccezioni') },
          { id: 'ticket', label: t('Ticket') },
          { id: 'ciclo-di-vita', label: t('Ciclo di vita') },
          { id: 'report', label: t('Report') },
          { id: 'finestre', label: t('Finestre') },
          { id: 'sla', label: t('Politica SLA') },
        ]}
      >
        <PageSection id={SECTION_ID.overview}>
          {summary.data ? (
            <Overview data={summary.data} canWrite={canWrite} onNewJob={(p) => setWizard(p ?? {})} onGo={go} />
          ) : !summary.error && <Spinner />}
        </PageSection>
        <PageSection id={SECTION_ID.machines}>
          {fleet.data ? (
            <Machines key={machineFilter} data={fleet.data} initialFilter={machineFilter} canWrite={canWrite}
              onNewJob={(p) => setWizard(p ?? {})} />
          ) : !fleet.error && <Spinner />}
        </PageSection>
        <PageSection id="copertura">
          <Coverage />
        </PageSection>
        <PageSection id={SECTION_ID.patches}>
          {catalog.data ? (
            <PatchCatalog key={patchFilter} data={catalog.data} initialFilter={patchFilter} canWrite={canWrite}
              onNewJob={(p) => setWizard(p ?? {})} />
          ) : !catalog.error && <Spinner />}
        </PageSection>
        <PageSection id={SECTION_ID.jobs}>
          {jobs.data ? (
            <Jobs jobs={jobs.data} openJobId={openJob} canWrite={canWrite} onChanged={reloadAll}
              onNewJob={() => setWizard({})} />
          ) : !jobs.error && <Spinner />}
        </PageSection>
        <PageSection id="politiche">
          <Policies onOpenJob={(id) => go('jobs', id)} />
        </PageSection>
        <PageSection id="eccezioni">
          <Exceptions />
        </PageSection>
        <PageSection id="ticket">
          <Tickets />
        </PageSection>
        <PageSection id="ciclo-di-vita">
          <Lifecycle />
        </PageSection>
        <PageSection id="report">
          <Reports />
        </PageSection>
        <PageSection id="finestre">
          <MaintenanceWindows canWrite={canWrite} />
        </PageSection>
        <PageSection id="sla">
          <SlaPolicy onSaved={reloadAll} />
        </PageSection>
      </SectionedPage>
      {wizard && (
        <NewJobWizard prefill={wizard} fleet={fleet.data} catalog={catalog.data}
          onClose={() => setWizard(null)}
          onCreated={(job) => {
            setWizard(null)
            reloadAll()
            go('jobs', job.id)
          }} />
      )}
    </>
  )
}
